import { Component, Input, Output, EventEmitter, signal, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { Site, SiteStatus, TlColor } from './screens.types';

type ActuType = 'critique' | 'info' | 'retablissement' | 'surveillance' | 'degrade' | 'discussion';
interface Actu { id: number; type: ActuType; offsetMin: number; message: string; badge: string; }

@Component({
  selector: 'app-dashboard-screen',
  standalone: true,
  imports: [TranslatePipe],
  templateUrl: './dashboard-screen.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./dashboard-screen.component.scss'],
})
export class DashboardScreenComponent implements OnInit, OnDestroy {
  private autoTimer?: ReturnType<typeof setInterval>;
  private nowTimer?: ReturnType<typeof setInterval>;
  private touchStartX = 0;
  private readonly _now = signal(new Date());
  private mouseStartX = 0;
  private dragDelta = 0;
  @Input() scrollY = 0;
  @Output() openList = new EventEmitter<void>();
  @Output() openActus = new EventEmitter<void>();
  @Output() siteSelected = new EventEmitter<Site>();
  @Output() openAvisPatient = new EventEmitter<void>();
  @Output() openSatisfaction = new EventEmitter<void>();

  logoScale(): number { return Math.max(0.6, 1.08 - this.scrollY / 120); }

  readonly sites: Site[] = [
    {
      id: 'nantes-centre', nom: 'NANTES CENTRE', statut: 'critique',
      tensionLabel: 'TENSION CRITIQUE', descInfo: 'Dégradation continue',
      activite: 'très élevée', activiteLevel: 'critique', presenceEstimee: 38,
      alertes: [
        { text: 'Alerte envoyée', type: 'default' },
      ],
      showActionBtns: false,
      timeline: ['vert', 'vert', 'orange', 'orange', 'rouge', 'rouge'],
      summary: '"Tension continue depuis 45 min"',
    },
    {
      id: 'saint-herblain', nom: 'SAINT-HERBLAIN', statut: 'moderee',
      tensionLabel: 'TENSION MODÉRÉE', descInfo: 'Flux en augmentation',
      activite: 'importante', activiteLevel: 'moderee', presenceEstimee: 14,
      alertes: [{ text: 'Surveillance active', type: 'warn' }],
      showActionBtns: true,
      timeline: ['vert', 'vert', 'vert', 'orange', 'orange', 'orange'],
      summary: '"Tension depuis 30 min"',
    },
    {
      id: 'reze', nom: 'REZÉ', statut: 'fluide',
      tensionLabel: 'FLUIDE', descInfo: null,
      activite: 'normale', activiteLevel: 'fluide', presenceEstimee: 5,
      alertes: [{ text: 'Disponible pour soutien', type: 'ok' }],
      showActionBtns: false,
      timeline: ['vert', 'vert', 'vert', 'vert', 'vert', 'vert'],
      summary: '"Situation stable"',
    },
    {
      id: 'beaujoire', nom: 'NANTES BEAUJOIRE', statut: 'fluide',
      tensionLabel: 'FLUIDE', descInfo: null,
      activite: 'faible', activiteLevel: 'fluide', presenceEstimee: 3,
      alertes: [{ text: 'Disponible pour soutien', type: 'ok' }],
      showActionBtns: false,
      timeline: ['vert', 'vert', 'vert', 'vert', 'vert', 'vert'],
      summary: '"Situation stable"',
    },
    {
      id: 'carquefou', nom: 'CARQUEFOU', statut: 'moderee',
      tensionLabel: 'TENSION MODÉRÉE', descInfo: 'Flux en augmentation',
      activite: 'importante', activiteLevel: 'moderee', presenceEstimee: 16,
      alertes: [{ text: 'Surveillance active', type: 'warn' }],
      showActionBtns: true,
      timeline: ['vert', 'vert', 'vert', 'vert', 'orange', 'orange'],
      summary: '"Tension depuis 15 min"',
    },
    {
      id: 'orvault', nom: 'ORVAULT', statut: 'fluide',
      tensionLabel: 'FLUIDE', descInfo: null,
      activite: 'normale', activiteLevel: 'fluide', presenceEstimee: 7,
      alertes: [{ text: 'Disponible pour soutien', type: 'ok' }],
      showActionBtns: false,
      timeline: ['vert', 'vert', 'orange', 'vert', 'vert', 'vert'],
      summary: '"Situation stable"',
    },
    {
      id: 'vertou', nom: 'VERTOU', statut: 'fluide',
      tensionLabel: 'FLUIDE', descInfo: null,
      activite: 'faible', activiteLevel: 'fluide', presenceEstimee: 3,
      alertes: [{ text: 'Disponible pour soutien', type: 'ok' }],
      showActionBtns: false,
      timeline: ['vert', 'vert', 'vert', 'vert', 'vert', 'vert'],
      summary: '"Situation stable"',
    },
    {
      id: 'la-chapelle', nom: 'LA CHAPELLE', statut: 'ferme',
      tensionLabel: 'SITE FERMÉ', descInfo: 'Aucune activité',
      activite: '', activiteLevel: 'ferme', presenceEstimee: 0,
      alertes: [], showActionBtns: false,
      timeline: [],
      summary: '',
    },
  ];

  readonly currentSlide  = signal(0);
  readonly exitSlideIdx  = signal<number | null>(null);
  readonly isAnimating   = signal(false);
  readonly animDir       = signal<'left' | 'right'>('left');

  readonly actus: Actu[] = [
    { id: 1, type: 'discussion',    offsetMin: 0,  message: 'Discussion "Nantes Centre · coordination" ouverte',         badge: 'DISCUSSION'   },
    { id: 2, type: 'info',          offsetMin: 2,  message: 'Alerte envoyée — Carquefou dépasse capacité',               badge: 'ALERTE'       },
    { id: 3, type: 'degrade',       offsetMin: 5,  message: 'Mode dégradé activé — Saint-Herblain',                     badge: 'MODE DÉGRADÉ' },
    { id: 4, type: 'critique',      offsetMin: 12, message: 'Nantes Centre critique depuis 21 min',                     badge: 'CRITIQUE'     },
    { id: 5, type: 'surveillance',  offsetMin: 20, message: 'Carquefou - Ajustement manuel temps attente : +15 min',    badge: 'ATTENTE'      },
    { id: 6, type: 'retablissement',offsetMin: 42, message: 'Rezé redevenu fluide après renfort de Marie L.',           badge: 'RÉTABLISSEMENT'},
  ];

  timeOf(offsetMin: number): string {
    const d = new Date(this._now().getTime() - offsetMin * 60_000);
    return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  }

  ngOnInit() { this.startAuto(); this.nowTimer = setInterval(() => this._now.set(new Date()), 60_000); }
  ngOnDestroy() { clearInterval(this.autoTimer); clearInterval(this.nowTimer); }

  private startAuto() {
    clearInterval(this.autoTimer);
    this.autoTimer = setInterval(() => this.go('next'), 5000);
  }

  private go(dir: 'next' | 'prev') {
    if (this.isAnimating()) return;
    const len = this.activeSites().length;
    const d = dir === 'next' ? 'left' : 'right';
    this.exitSlideIdx.set(this.currentSlide());
    this.animDir.set(d);
    this.isAnimating.set(true);
    this.currentSlide.update(i => dir === 'next' ? (i + 1) % len : (i - 1 + len) % len);
    setTimeout(() => { this.isAnimating.set(false); this.exitSlideIdx.set(null); }, 380);
  }

  prevSlide() { this.go('prev'); this.startAuto(); }
  nextSlide() { this.go('next'); this.startAuto(); }

  onTouchStart(e: TouchEvent) { this.touchStartX = e.touches[0].clientX; }
  onTouchEnd(e: TouchEvent) {
    const delta = e.changedTouches[0].clientX - this.touchStartX;
    if (Math.abs(delta) < 30) return;
    delta < 0 ? this.nextSlide() : this.prevSlide();
  }

  private actusDragX = 0;
  private actusScrollStart = 0;
  private actusDragging = false;

  onActusMouseDown(e: MouseEvent, el: HTMLElement) {
    this.actusDragging = true;
    this.actusDragX = e.clientX;
    this.actusScrollStart = el.scrollLeft;
  }
  onActusMouseMove(e: MouseEvent, el: HTMLElement) {
    if (!this.actusDragging) return;
    e.preventDefault();
    el.scrollLeft = this.actusScrollStart - (e.clientX - this.actusDragX);
  }
  onActusMouseUp() { this.actusDragging = false; }

  onMouseDown(e: MouseEvent) { e.preventDefault(); this.mouseStartX = e.clientX; this.dragDelta = 0; }
  onMouseUp(e: MouseEvent) {
    this.dragDelta = e.clientX - this.mouseStartX;
    if (Math.abs(this.dragDelta) >= 30) {
      this.dragDelta < 0 ? this.nextSlide() : this.prevSlide();
    }
  }
  onMouseLeave() { this.dragDelta = 0; }
  onCardClick(site: Site) { if (Math.abs(this.dragDelta) > 8) return; this.siteSelected.emit(site); }

  tlColor(c: TlColor): string {
    const map: Record<TlColor, string> = { vert: '#10B981', orange: '#F59E0B', rouge: '#EF4444' };
    return map[c];
  }

  getTimelineSlots(): string[] {
    const now = new Date();
    const slots: string[] = [];
    for (let i = 5; i >= 0; i--) {
      const t = new Date(now.getTime() - i * 30 * 60000);
      slots.push(`${String(t.getHours()).padStart(2,'0')}:${String(Math.floor(t.getMinutes()/15)*15).padStart(2,'0')}`);
    }
    return slots;
  }

  critiques()    { return this.sites.filter(s => s.statut === 'critique').length; }
  moderees()     { return this.sites.filter(s => s.statut === 'moderee').length; }
  fluides()      { return this.sites.filter(s => s.statut === 'fluide').length; }
  activeSites()  { return this.sites.filter(s => s.statut !== 'ferme'); }
  tensionSites() { return this.sites.filter(s => s.statut === 'critique' || s.statut === 'moderee'); }
  fluideSites()  { return this.sites.filter(s => s.statut === 'fluide'); }
  critiqueSite() { return this.sites.find(s => s.statut === 'critique') ?? null; }
}